import CheckSquare from 'assets/icons/CheckSquare';
import CalendarDateIcon from 'assets/icons/CalendarDateIcon';
import AccountabilityBuddy from 'assets/icons/AccountabilityBuddy';
import Notepad from 'assets/icons/revamp/notepad';
import Miscellaneous from 'assets/icons/Miscellaneous';
import People from 'assets/icons/People';
import Repeat from 'assets/icons/Repeat';
import ShieldUser from 'assets/icons/ShieldUser';
import Widget from 'assets/icons/Widget';
import Book from 'assets/icons/revamp/book';
import Apps from 'assets/icons/revamp/apps';
import DownloadApps from 'assets/icons/DownloadApps';
import PaletteIcon from 'assets/icons/PaletteIcon';
import { NAV_GROUP_COLLAPSED_STORAGE_KEY } from 'constants/navBar';
import { ROUTES } from 'constants/routes';
import { ATTRIB } from 'constants/test';
import { NavGroupId } from 'enums';
import { DownloadAppsLocationState, MenuItem } from 'interfaces';
import { NavigateOptions } from 'react-router-dom';
import { t } from 'services/i18n.service';
import { formatDayOfMonth } from 'utils/time.util';

export interface NavGroup {
  id: NavGroupId;
  label: string;
  panelItems: MenuItem[];
  railItems: MenuItem[];
}

const EXTRA_ICON_BY_PATH: Record<string, JSX.Element> = {
  [ROUTES.LIBRARY]: <Book />,
  [ROUTES.INTEGRATIONS]: <Apps />,
  [ROUTES.DOWNLOAD_APPS]: <DownloadApps />
};

const MY_CIRCLE_ICON_BY_PATH: Record<string, JSX.Element> = {
  [ROUTES.ACCOUNTABILITY_BUDDY]: <AccountabilityBuddy />,
  [ROUTES.TEAM]: <People />,
  [ROUTES.MANAGED_BLOCKING]: <ShieldUser />
};

export const getItemKey = (item: MenuItem): string =>
  item.path ?? item.text ?? '';

// Download Apps renders full-screen without the sidebar, so in-app opens are
// flagged to give the page a close button. The post-signup redirect lands there
// without this state on purpose: onboarding has nowhere to go back to.
export const getNavigateOptions = (
  path: string
): NavigateOptions | undefined => {
  if (path !== ROUTES.DOWNLOAD_APPS) return undefined;
  const state: DownloadAppsLocationState = { isOpenedFromNav: true };
  return { state };
};

export const getNavGroupPanelId = (id: NavGroupId): string =>
  `nav-group-panel-${id}`;

const pathMatchesLocation = (itemPath: string, pathname: string): boolean => {
  if (pathname.endsWith(itemPath)) return true;
  if (!itemPath.includes(':')) return false;
  const basePath = itemPath.slice(0, itemPath.indexOf('/:'));
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
};

export const itemMatchesPath = (item: MenuItem, pathname: string): boolean => {
  if (item.path && pathMatchesLocation(item.path, pathname)) return true;
  return item.subMenus?.some((sub) => itemMatchesPath(sub, pathname)) ?? false;
};

export const groupContainsPath = (
  group: NavGroup,
  pathname: string
): boolean => group.panelItems.some((item) => itemMatchesPath(item, pathname));

export const findActiveGroupId = (
  groups: NavGroup[],
  pathname: string
): NavGroupId | null => {
  const match = groups.find((group) => groupContainsPath(group, pathname));
  return match?.id ?? null;
};

export const readCollapsedGroups = (): NavGroupId[] => {
  try {
    const raw = localStorage.getItem(NAV_GROUP_COLLAPSED_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const validIds = Object.values(NavGroupId) as string[];
    return parsed.filter((id): id is NavGroupId => validIds.includes(id));
  } catch {
    return [];
  }
};

export const persistCollapsedGroups = (collapsedGroups: NavGroupId[]): void => {
  try {
    localStorage.setItem(
      NAV_GROUP_COLLAPSED_STORAGE_KEY,
      JSON.stringify(collapsedGroups)
    );
  } catch {
    // Ignore write failures (e.g. storage disabled/full).
  }
};

const buildPlanItems = (selectedDay: string): MenuItem[] => [
  {
    text: t('day_plan.day_plan'),
    path: ROUTES.DAY_PLAN,
    attribAction: ATTRIB.TEST.DAY_PLAN,
    icon: <CalendarDateIcon day={formatDayOfMonth(selectedDay)} />
  },
  {
    text: t('settings'),
    path: ROUTES.SETTINGS,
    attribAction: ATTRIB.TEST.SETTINGS,
    icon: <Repeat />
  },
  {
    text: t('project.projects'),
    path: ROUTES.PROJECTS,
    attribAction: ATTRIB.TEST.PROJECTS,
    icon: <PaletteIcon />
  },
  {
    text: t('nav_tasks'),
    path: ROUTES.TODO,
    attribAction: ATTRIB.TEST.TODO,
    icon: <CheckSquare />
  },
  {
    text: t('stats_page.notes'),
    path: ROUTES.NOTES,
    icon: <Notepad />
  }
];

const withIconsByPath = (
  items: MenuItem[],
  iconsByPath: Record<string, JSX.Element>
): MenuItem[] =>
  items.map((item) => ({
    ...item,
    icon: item.path ? iconsByPath[item.path] : item.icon
  }));

const buildAdminItem = (isUserAdmin: boolean): MenuItem | null =>
  isUserAdmin
    ? {
        text: t('admin.admin'),
        path: ROUTES.ADMIN,
        attribAction: ATTRIB.TEST.ADMIN,
        icon: <ShieldUser />
      }
    : null;

interface BuildNavGroupsParams {
  selectedDay: string;
  myCircleSubMenus: MenuItem[];
  extraMenuSubMenus: MenuItem[];
  isUserAdmin: boolean;
}

const buildMarketplaceItem = (): MenuItem => ({
  text: t('market.marketplace'),
  path: ROUTES.MARKETPLACE,
  attribAction: ATTRIB.TEST.MARKETPLACE,
  icon: <Widget />
});

const buildMyCircleGroup = (myCircleSubMenus: MenuItem[]): NavGroup => ({
  id: NavGroupId.MY_CIRCLE,
  label: t('my_circle'),
  panelItems: withIconsByPath(myCircleSubMenus, MY_CIRCLE_ICON_BY_PATH),
  railItems: [
    {
      text: t('my_circle'),
      subMenus: myCircleSubMenus,
      icon: <People />
    }
  ]
});

const buildExtraGroup = (extraMenuSubMenus: MenuItem[]): NavGroup => {
  const marketplaceItem = buildMarketplaceItem();
  return {
    id: NavGroupId.EXTRA,
    label: t('extra'),
    panelItems: [
      marketplaceItem,
      ...withIconsByPath(extraMenuSubMenus, EXTRA_ICON_BY_PATH)
    ],
    railItems: [
      marketplaceItem,
      {
        text: t('extra'),
        subMenus: extraMenuSubMenus,
        icon: <Miscellaneous />
      }
    ]
  };
};

export const buildNavGroups = ({
  selectedDay,
  myCircleSubMenus,
  extraMenuSubMenus,
  isUserAdmin
}: BuildNavGroupsParams): { navGroups: NavGroup[]; adminItem: MenuItem | null } => {
  const planItems = buildPlanItems(selectedDay);
  return {
    navGroups: [
      {
        id: NavGroupId.PLAN_AND_DO,
        label: t('nav_group_plan_and_do'),
        panelItems: planItems,
        railItems: planItems
      },
      buildMyCircleGroup(myCircleSubMenus),
      buildExtraGroup(extraMenuSubMenus)
    ],
    adminItem: buildAdminItem(isUserAdmin)
  };
};
